import moment                    from 'moment';
import {Injectable}                   from '@angular/core';
import {CropApplicationStore}         from '../../stores/task-management/crop-application.store';
import {CropTypeStore}                from '../../stores/base-data/crop.types.store';
import {ApTranslationService}         from '../../ap-utils/service/ap-translation.service';
import {PlantProtectionPlanBookStore} from '../../stores/plant-protection/plant-protection-planbook.store';
import {UnitsStore}                   from '../../stores/common/units.store';
import {GetRoundNumericPipe}          from '../../ap-utils';
import {WorkTypes}                    from '../../stores/base-data/worrktypes.store';
import {OperationModesEnum}           from '../../stores/common/operation-modes.store';
import {ApDateService, DateSort}      from '../../ap-core/services/ap-date-service';
import {ApTooltipContentService}      from '../../ap-utils/service/ap-tooltip-content.service';
import {ApplicationDatesData}         from '../types/pp-common.types';
import {LanguageStore}                from '../../stores/translation/language.store';
import IPlantProtectionSummary = Data.TaskManagement.IPlantProtectionSummary;
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;
import IPlantProtectionProductList = Data.TaskManagement.IPlantProtectionProductList;
import {ObjectFactory}                from 'ts-tooling';
import {GetRoundNumericService}       from '../../ap-utils/service/get-round-numeric.service';

/**
 * Service for common plant protection calculations
 */
@Injectable({
  providedIn: 'root'
})
export class PpCommonService {
  constructor(private unitsStore: UnitsStore,
              private cropTypeStore: CropTypeStore,
              private cropApplicationStore: CropApplicationStore,
              private plantProtectionPlanBookStore: PlantProtectionPlanBookStore,
              private dateService: ApDateService,
              private languageStore: LanguageStore,
              private translationService: ApTranslationService,
              private tooltipContentService: ApTooltipContentService,
              private roundNumericPipe: GetRoundNumericPipe,
              private roundNumericService: GetRoundNumericService) {
  }

  /**
   * Returns the application number name (1. Gabe, ...)
   */
  public getApplNumName(application: number, shortVersion?: boolean): string {
    switch (application) {
      case -1:
        return `${this.translationService.translate('Global__Scan')}`;
      case 0:
        if (shortVersion) {
          return this.translationService.translate('Global__P0');
        } else {
          return `${this.translationService.translate('AgriConnect_ApplNum_0')}`;
        }
      case 1:
        if (shortVersion) {
          return this.translationService.translate('Global__P1');
        } else {
          return `${this.translationService.translate('Global__1st')} ${this.translationService.translate('Global__ApplNum')}`;
        }
      case 2:
        if (shortVersion) {
          return this.translationService.translate('Global__P2');
        } else {
          return `${this.translationService.translate('Global__2nd')} ${this.translationService.translate('Global__ApplNum')}`;
        }
      case 3:
        if (shortVersion) {
          return this.translationService.translate('Global__P3');
        } else {
          return `${this.translationService.translate('Global__3rd')} ${this.translationService.translate('Global__ApplNum')}`;
        }
      case 4:
        if (shortVersion) {
          return this.translationService.translate('Global__P4');
        } else {
          return `${this.translationService.translate('Global__4th')} ${this.translationService.translate('Global__ApplNum')}`;
        }
    }
    return '';
  }

  /**
   * Calculates sum of applied water based on summary operation mode
   */
  public getTotalWaterAmountBasedOnOperationMode(planBooks: IPlantProtectionPlanBook[]): number | undefined {
    if (!planBooks || planBooks.length === 0) {
      return undefined;
    }
    const operationMode = planBooks.FirstOrDefault()?.Summary?.OperationMode?.Key;
    if (operationMode !== OperationModesEnum.PpTarget
      && operationMode !== OperationModesEnum.PpTargetMapPerField
      && operationMode !== OperationModesEnum.PPTargetMapPerPlan
      && operationMode !== OperationModesEnum.Constant) {
      return undefined;
    }
    const waterAmount = this.calculateWaterAmountOfPlanBooks(planBooks);
    if (waterAmount === null) {
      return undefined;
    }
    return waterAmount;
  }

  /**
   * Calculates applied water based on summary operation mode
   */
  public getWaterAmountBasedOnOperationMode(planBook: IPlantProtectionPlanBook): number | undefined {
    if (!planBook) {
      return undefined;
    }
    const operationMode = planBook.Summary?.OperationMode?.Key;
    if (operationMode !== OperationModesEnum.PpTarget
      && operationMode !== OperationModesEnum.PPTargetMapPerPlan
      && operationMode !== OperationModesEnum.PpTargetMapPerField
      && operationMode !== OperationModesEnum.Constant) {
      return undefined;
    }
    const waterAmount = this.calculateWaterAmountOfPlanBook(planBook);
    if (waterAmount === null) {
      return undefined;
    }
    return waterAmount;
  }

  /**
   * Calculates sum of applied water over all plant protection planBooks
   */
  public calculateWaterAmountOfPlanBooks(planBooks: IPlantProtectionPlanBook[] | null): number | null {
    if (!planBooks || planBooks.length <= 0) {
      return null;
    }
    return planBooks.reduce((sum, planBook) => {
      const waterAmount = this.calculateWaterAmountOfPlanBook(planBook) ?? 0;
      return sum + waterAmount;
    }, 0);
  }

  public calculateWaterAmountOfPlanBook(planBook: IPlantProtectionPlanBook | null): number | null {
    if (!planBook) {
      return null;
    }
    const applicationValue = this.getApplicationValue(planBook);
    return (applicationValue * (planBook?.PlannedArea) / 10000) || 0;
  }

  public getApplicationValue(planBook: IPlantProtectionPlanBook | null): number | null {
    if (!planBook) {
      return null;
    }
    return planBook.OperationMode?.Key === OperationModesEnum.Constant
      ? planBook.ApplicationConst
      : planBook.ApplicationRate;
  }

  public isWorkTypeSpraying(planBook: IPlantProtectionPlanBook): boolean {
    return planBook.ProductList?.FirstOrDefault()?.ProductListDetails?.WorkType === WorkTypes.Spraying;
  }

  /**
   * Calculates the summary's application number ('Gabe')
   */
  public getApplNumFromSummary(summary: IPlantProtectionSummary): number {
    if (!summary) {
      return 0;
    }
    const planBooks = this.plantProtectionPlanBookStore.getPlanningsBySummaryId(summary.Id);
    const eppoCode = CropApplicationStore._getMost(planBooks.map((pB) => this.cropTypeStore.getDefaultCroptype(pB?.MainCropId)?.Eppocode));
    return this.cropApplicationStore.GetApplication(eppoCode, summary.EcValue, summary.OperationMode);
  }

  /**
   * Builds a string expression of all used products and there appl_rates
   */
  public getAppliedProductsExpr(products: IPlantProtectionProductList[], isTooltip?: boolean, separator?: string): string {
    if (products?.length <= 0) {
      return '';
    }
    const joinChar = separator ?? (isTooltip ? '<br>' : '\n');
    return products?.map(p => this.buildProductExpr(p))?.join(joinChar);
  }

  /**
   * Builds a string expression of the sum of all used products and there appl_rates
   */
  public getAppliedProductSumExpr(products: IPlantProtectionProductList[], options:
    { areaHa?: number, withoutProdName?: boolean, isTooltip?: boolean }): string {
    if (products?.length <= 0 || options?.areaHa <= 0) {
      return '';
    }
    const joinChar = options?.isTooltip ? '<br>' : '\n';
    return products?.map(p => this.buildProductExpr(p, {
      areaHa: options?.areaHa,
      withoutProdName: options?.withoutProdName,
      useBaseUnit: true
    }))?.join(joinChar);
  }

  /**
   * Creates a list of distinct products which are used in the planBooks
   */
  public getDistinctProductsOfPlanBooks(planBooks: IPlantProtectionPlanBook[]): IPlantProtectionProductList[] {
    const appliedProducts: IPlantProtectionProductList[] = [];
    for (const productListItem of planBooks.map(pB => pB.ProductList).flat()) {
      const planBook = planBooks.find(p => p.Id === productListItem.PlanId);
      if (!appliedProducts.Any(p => p.ProductListDetails?.Id === productListItem.ProductListDetails?.Id)) {
        // Optimisation, this happens for PP Target rate map modes
        if (planBook.ApplicationRate !== planBook.Summary.UserApplicationRate) {
          const productListItemCopy = ObjectFactory.Copy(productListItem);
          productListItemCopy.ProductRate = this.roundNumericService.roundAsNumber(planBook.ApplicationRate * productListItem.ProductRate / planBook.Summary.UserApplicationRate);
          appliedProducts.push(productListItemCopy);
        } else {
          appliedProducts.push(productListItem);
        }
      }
    }
    return appliedProducts;
  }

  public getApplicationDatesData(planBooks: IPlantProtectionPlanBook[]): ApplicationDatesData | null {
    const planBooksApplicationDates = this._getPlanBookApplicationDates(planBooks);
    const uniqueApplicationDates = this.dateService.getUniqDates(planBooksApplicationDates, DateSort.Ascending);
    return {
      Tooltip: this.tooltipContentService.planBooksApplicationDatesTooltip(uniqueApplicationDates.map(x => ({ApplicationDate: x}))),
      IsMoreThanOneUniqDate: uniqueApplicationDates.length > 1,
      NewestDate: uniqueApplicationDates[uniqueApplicationDates.length - 1]
    };
  }

  public buildProductExpr(product: IPlantProtectionProductList,
                          options?: { areaHa?: number, withoutProdName?: boolean, useBaseUnit?: boolean }): string {
    let unit = this.unitsStore?.getUnitWithUnit(product?.Unit?.Id);
    if (options?.useBaseUnit && unit?.BaseUnit) {
      unit = unit.BaseUnit;
    }
    const prodName = options?.withoutProdName ? '' : product?.Product?.ProductName;
    return `${this.roundNumericPipe.transform((product?.ProductRate ?? 0) * (options?.areaHa ?? 1))} ${this.translationService.translate(unit?.Key)} ${prodName}`;
  }

  private _getPlanBookApplicationDates(planBooks: IPlantProtectionPlanBook[]): Date[] {
    if (!planBooks || planBooks.length <= 0) {
      return [];
    }
    const planBooksApplicationDates = planBooks.filter(x => x.ApplicationDate)
      .map(x => x.ApplicationDate);
    return planBooksApplicationDates.map(x => {
      const farmDate = this.dateService.toFarmDate(new Date(x)).toDate();
      return this.dateService.getDateMidnight(farmDate);
    });
  }

  public getPlanningDescription(planBook: IPlantProtectionPlanBook): string {
    return planBook.Summary.Title + ' ' + moment(planBook.ApplicationDate, this.languageStore.IsoDateTimeFormat).format(this.languageStore.CurrentDateTimeFormat);
  }
}
